import { Head, Link, router } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import { formatDate } from "@/lib/date-utils"
import type { BreadcrumbItem } from "@/types"

interface PtoRequest {
  id: number
  user_name: string | null
  request_type: number
  request_type_name: string
  start_date: string | null
  end_date: string | null
  hours_requested: number
  status: number
  status_name: string
  approved_by: string | null
  approved_at: string | null
}

interface YtdSummary {
  year: number
  pto_credits: number
  pto_used: number
  pto_remaining: number
  sto_used: number
  unavailable_days: number
}

interface Props {
  pto_requests: PtoRequest[]
  can_approve: boolean
  request_types: Record<number, string>
  ytd_summary: YtdSummary
}

export default function Index({
  pto_requests,
  can_approve,
  ytd_summary,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "PTO Requests", href: "/pto_requests" },
  ]

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to cancel this PTO request?")) {
      router.delete(`/pto_requests/${id}`)
    }
  }

  const handleApprove = (id: number) => {
    router.patch(`/pto_requests/${id}/approve`)
  }

  const handleDeny = (id: number) => {
    const notes = prompt("Enter reason for denial (optional):")
    router.patch(`/pto_requests/${id}/deny`, { approval_notes: notes })
  }

  const statusBadge = (status: number, statusName: string) => {
    const colors: Record<number, string> = {
      0: "bg-hpi-yellow/30 text-hpi-dark-gray", // Pending
      1: "bg-hpi-dark-green/20 text-hpi-dark-green", // Approved
      2: "bg-hpi-red/20 text-hpi-red", // Denied
      99: "bg-hpi-pale-gray text-hpi-dark-gray", // Cancelled
    }
    return (
      <span
        className={`rounded px-2 py-1 text-xs ${colors[status] || "bg-hpi-pale-gray text-hpi-dark-gray"}`}
      >
        {statusName}
      </span>
    )
  }

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="PTO Requests" />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">PTO Requests</h1>
          <div className="flex gap-2">
            {can_approve && (
              <Link
                href="/pto_credits"
                className="border-hpi-dark-blue text-hpi-dark-blue hover:bg-hpi-pale-blue rounded border px-4 py-2"
              >
                Manage Credits
              </Link>
            )}
            <Link
              href="/pto_requests/new"
              className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
            >
              Request PTO
            </Link>
          </div>
        </div>

        {(ytd_summary.pto_credits > 0 ||
          ytd_summary.pto_used > 0 ||
          ytd_summary.sto_used > 0 ||
          ytd_summary.unavailable_days > 0) && (
          <div className="border-hpi-light-gray bg-hpi-pale-gray mb-4 flex flex-wrap gap-x-6 gap-y-1 rounded border p-4">
            {ytd_summary.pto_credits > 0 && (
              <div>
                <span className="font-semibold">
                  {ytd_summary.year} PTO Credits:
                </span>{" "}
                {ytd_summary.pto_credits} hours
                {ytd_summary.pto_remaining > 0 && (
                  <span className="text-hpi-med-gray">
                    {" "}
                    ({ytd_summary.pto_remaining} remaining)
                  </span>
                )}
              </div>
            )}
            {ytd_summary.pto_used > 0 && (
              <div>
                <span className="font-semibold">{ytd_summary.year} PTO:</span>{" "}
                {ytd_summary.pto_used} hours
              </div>
            )}
            {ytd_summary.sto_used > 0 && (
              <div>
                <span className="font-semibold">{ytd_summary.year} STO:</span>{" "}
                {ytd_summary.sto_used} hours
              </div>
            )}
            {ytd_summary.unavailable_days > 0 && (
              <div>
                <span className="font-semibold">
                  {ytd_summary.year} Unavailable:
                </span>{" "}
                {ytd_summary.unavailable_days} days
              </div>
            )}
          </div>
        )}

        {pto_requests.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border text-sm">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Employee
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Type
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Start Date
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  End Date
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-right">
                  Hours
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-center">
                  Status
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Approved By
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {pto_requests.map((request) => (
                <tr key={request.id} className="hover:bg-hpi-pale-blue">
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {request.user_name}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {request.request_type_name}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {formatDate(request.start_date)}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {formatDate(request.end_date)}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2 text-right">
                    {request.hours_requested.toFixed(2)}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2 text-center">
                    {statusBadge(request.status, request.status_name)}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {request.approved_by ?? "-"}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/pto_requests/${request.id}`}
                      className="text-hpi-active-blue hover:text-hpi-active-blue mr-2"
                    >
                      View
                    </Link>
                    {request.status === 0 && (
                      <>
                        <Link
                          href={`/pto_requests/${request.id}/edit`}
                          className="text-hpi-active-blue hover:text-hpi-active-blue mr-2"
                        >
                          Edit
                        </Link>
                        {can_approve && (
                          <>
                            <button
                              onClick={() => handleApprove(request.id)}
                              className="text-hpi-dark-green hover:text-hpi-dark-green mr-2"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => handleDeny(request.id)}
                              className="text-hpi-red hover:text-hpi-red mr-2"
                            >
                              Deny
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDelete(request.id)}
                          className="text-hpi-med-gray hover:text-hpi-med-gray"
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-hpi-med-gray">No PTO requests found.</p>
        )}
      </div>
    </AppLayout>
  )
}
